// domain/services/serviceCodeValidity.js

import { SERVICE_CODE_ORIGIN } from '../constants/serviceCode';
import { isSameCodeString } from './codeString';
import { isActiveAsOf, toAsOfDate, toDateStr } from './effectiveDating';

/**
 * Is a code billable for a given service date, and which of several codes sharing a string is?
 *
 * PURE, and takes its `asOf` as an argument rather than reading a clock — the rule that makes every
 * as-of question in this context testable without freezing time, and that stops two reads in one
 * session disagreeing across midnight. Callers at the edge decide what "now" means.
 *
 * WHY THIS IS NOT THE VERSION-SERIES RESOLVER. `VersionTimeline` answers "which restatement of this
 * record applies", picking the last one to have started. That resolution is a search over candidates
 * and needs the flag and the reduce that go with it. This one asks a closed yes/no question of a
 * single record's window, because a code is not restated — a differently-dated code is a DIFFERENT
 * code. The two look similar and must not be merged: giving codes a fall-forward would silently bill
 * a 2026 service under a 2027 revision, which is exactly what distinct identities prevent.
 */

/**
 * May this code be billed for this service date?
 *
 * Both bounds inclusive: `validFrom` is the first billable date, `retiredOn` the last. A null
 * `retiredOn` is an open end and the ordinary state, not a gap.
 *
 * A code whose `validFrom` cannot be read as a calendar date is NOT VALID on any date. Dropping it
 * is the only safe reading — a window with an unreadable start has no position, and treating it as
 * open would make one malformed record billable for all of history. The collection's writers
 * validate on the way in, so this guards a hand-edited or mis-seeded document rather than a normal
 * one. Tombstones are excluded here too, since a tombstoned code was never a real code.
 *
 * AN UNREADABLE `retiredOn` FAILS THE SAME WAY, and the distinction from an absent one is the point:
 * absent is an open end and the ordinary state, but a value somebody set and this cannot read is a
 * mis-seeded record, and reading it as open would keep a code billable forever on the strength of a
 * typo — the one direction of this mistake nobody notices. `findCatalogCode` in the migration
 * classifier draws the same line, and the two have to agree or a service migrates to a code the
 * picker then refuses to resolve.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode} serviceCode
 * @param {string} asOf - 'YYYY-MM-DD' the service date
 * @returns {boolean}
 */
export function isBillableOn(serviceCode, asOf) {
  if (serviceCode === null || serviceCode === undefined) {
    return false;
  }
  if (serviceCode.isDeleted === true) {
    return false;
  }
  const validFrom = toDateStr(serviceCode.validFrom);

  if (validFrom === null) {
    return false;
  }
  const isOpenEnded = serviceCode.retiredOn === null || serviceCode.retiredOn === undefined;
  const retiredOn = isOpenEnded ? null : toDateStr(serviceCode.retiredOn);

  if (!isOpenEnded && retiredOn === null) {
    return false;
  }
  return isActiveAsOf(asOf, validFrom, retiredOn);
} // isBillableOn()

/**
 * Has this code stopped applying, as of a date? The complement of "still open", not of `isBillableOn`.
 *
 * DELIBERATELY NOT `!isBillableOn`. A code that has not started yet is also not billable, and calling
 * that "retired" would show a scheduled successor as expired in every list it appears in — the two
 * ends of a window fail differently and a screen has to tell them apart.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode} serviceCode
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {boolean}
 */
export function isRetiredOn(serviceCode, asOf) {
  const retiredOn = toDateStr(serviceCode?.retiredOn);
  const asOfDate = toAsOfDate(asOf);

  if (retiredOn === null || asOfDate === null) {
    return false;
  }
  return asOfDate > retiredOn;
} // isRetiredOn()

/**
 * The one code valid for a service date out of several sharing a code string.
 *
 * FOR HUMANS AND MIGRATIONS, NEVER FOR BILLING. Billing resolves a code by `id`, because that is the
 * reference a treatment service stores and the whole reason identity is not the string. This exists
 * for the two places where only a string is available: a migration reading a legacy free-text field,
 * and a person typing a code they read off a payer's letter.
 *
 * RETURNS NULL WHEN NOTHING MATCHES, and never falls back to "the only one" or "the newest". A
 * string with no code valid on the date is an honest "we do not have that code for that day" — the
 * fallback would silently resolve a 2026 service to a 2027 revision, which is the failure the
 * distinct identities exist to prevent, reintroduced at the lookup.
 *
 * RETURNS NULL WHEN MORE THAN ONE MATCHES WITHIN AN ORIGIN, rather than picking. Windows for one
 * string are expected to be disjoint there, but nothing structural enforces it — a mis-seeded
 * overlap is possible, and arbitrating between two codes that both claim a date is exactly the guess
 * this context refuses to make. A caller that gets null because of an overlap sees the same honest
 * hold as one that gets it because nothing matched, and the data is where the overlap gets fixed.
 *
 * MANAGED WINS ACROSS ORIGINS, and this is the one place the two sets are ranked rather than merged.
 * An org may mint a code whose string collides with one we govern — the mint flow advises against it
 * and deliberately does not block — so on a mixed set `97153` can legitimately match two records at
 * once. Treating that as an ambiguity would return null and break a lookup that resolved correctly
 * the day before the org minted anything: a shadow would silently disable resolution of OUR code,
 * for that clinic, with nothing to show why. So the managed candidates are consulted first and the
 * custom ones only when none matched. Ranking beats arbitration here precisely because the two sets
 * are not peers — one is the governed identity that benchmarks and analytics key on.
 *
 * WHICH IS NOT A CONTRADICTION OF THE RULE ABOVE. Refusing to arbitrate protects a decision the data
 * cannot make: two records of equal standing, and no principle to choose between them. Here there
 * is a principle, it is stated, and it always points the same way.
 *
 * CASE-INSENSITIVE, because HCPCS codes carry a letter and a person typing `h0032` means `H0032`.
 * This must stay in step with `findCatalogCode` in the migration classifier, which normalizes the
 * same way — the two answer the same question on either side of a boundary, and a service the
 * migration matched on a lower-case string would otherwise fail to resolve here. That classifier
 * runs server-side across every clinic and must keep matching MANAGED codes only; a custom code has
 * no meaning outside its own clinic, so matching one there would attach a service to a code from
 * somebody else's list.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode[]} serviceCodes - candidates; any subset
 *   of the codes available to one clinic, not necessarily sharing a string
 * @param {string} code - the code string to match, trimmed and compared without regard to case
 * @param {string} asOf - 'YYYY-MM-DD' the date the code must be valid on
 * @returns {import('../model/ServiceCode.types').ServiceCode|null}
 */
export function findByCodeOn(serviceCodes, code, asOf) {
  const matches = (serviceCodes ?? []).filter((serviceCode) => (
    isSameCodeString(serviceCode?.code, code) && isBillableOn(serviceCode, asOf)
  ));
  const managed = matches.filter(
    (serviceCode) => serviceCode.origin === SERVICE_CODE_ORIGIN.MANAGED,
  );
  const ranked = managed.length > 0 ? managed : matches;

  return ranked.length === 1 ? ranked[0] : null;
} // findByCodeOn()
